// web/src/components/AuditFeed.tsx — OWNED BY S4 (RUBRIC R3.3 / R7.4).
// Live tamper-evident audit timeline (audit.appended over WS), newest-first,
// rendered as a connected timeline. "Verify chain" surfaces the R3.2 result
// (ok / broken@seq). Reimagined 2026-06 (timeline, was a flat list).
//
// NOTE (S4 contract tweak): onVerify resolves the verify payload so the result
// can be shown inline, rather than the stub's Promise<void>.
import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import type { AuditEntry, AuditKind } from '../../../shared/types';
import { EmptyState, FeedbackMessage } from './ui';
import { Icon } from './ui/Icon';

export interface VerifyResult {
  ok: boolean;
  count: number;
  brokenAtSeq: number | null;
}

export interface AuditFeedProps {
  entries: AuditEntry[];
  onVerify: () => Promise<VerifyResult>;
}

// Timeline dot colour per audit kind (token vars). Partial: the zero-trust
// quorum kinds (ADR-002) fall back to the neutral token until S4 themes them.
const KIND_COLOR: Partial<Record<AuditKind, string>> = {
  created: 'var(--color-accent)',
  requested: 'var(--color-blue)',
  approved: 'var(--color-green)',
  revealed: 'var(--color-amber)',
  denied: 'var(--color-red)',
  revoked: 'var(--color-red)',
  expired: 'var(--color-text3)',
};
const DEFAULT_KIND_COLOR = 'var(--color-text3)';

export function AuditFeed({ entries, onVerify }: AuditFeedProps): React.JSX.Element {
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<VerifyResult | null>(null);

  // Newest first; entries arrive over WS in append order (ascending seq).
  const ordered = [...entries].sort((a, b) => b.seq - a.seq);

  async function verify(): Promise<void> {
    setVerifying(true);
    setResult(null);
    try {
      setResult(await onVerify());
    } catch {
      setResult({ ok: false, count: 0, brokenAtSeq: null });
    } finally {
      setVerifying(false);
    }
  }

  return (
    <section className="bgSection" aria-label="Audit feed">
      <header className="bgSectionHead">
        <div>
          <div className="kicker">Tamper-evident</div>
          <h2 className="bgSectionTitle">Audit timeline</h2>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={verify} disabled={verifying}>
          {verifying ? (
            <span className="spinner" aria-hidden="true" />
          ) : (
            <>
              <Icon icon={ShieldCheck} size="sm" /> Verify chain
            </>
          )}
        </button>
      </header>

      {result && (
        <div style={{ padding: '0.75rem 1.15rem 0' }}>
          <FeedbackMessage
            ok={result.ok}
            msg={
              result.ok
                ? `Chain intact — ${result.count} entries verified`
                : `Chain BROKEN${result.brokenAtSeq !== null ? ` at seq ${result.brokenAtSeq}` : ''}`
            }
          />
        </div>
      )}

      {ordered.length === 0 ? (
        <EmptyState title="No audit entries" subtitle="Lifecycle events stream here live" />
      ) : (
        <ul className="bgTimeline" aria-live="polite">
          {ordered.map((e) => (
            <li key={e.id} className="bgTl fade-up">
              <span
                className="bgTlDot"
                style={{ '--tl-color': KIND_COLOR[e.kind] ?? DEFAULT_KIND_COLOR } as React.CSSProperties}
                aria-hidden="true"
              />
              <span className="bgTlSeq">#{e.seq}</span>
              <span
                className="bgTlKind"
                style={{ '--tl-color': KIND_COLOR[e.kind] ?? DEFAULT_KIND_COLOR } as React.CSSProperties}
              >
                {e.kind}
              </span>
              <span className="bgTlActor">{e.actorName}</span>
              <span className="bgTlTime">
                {new Date(e.createdAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default AuditFeed;
